<template>
  <div class="nn-fond" @click.self="fermer">
    <div class="nn-boite card shadow">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-balloon-heart me-2 text-primary"></i>Enregistrer un nouveau-né</h5>
        <button type="button" class="btn-close" @click="fermer" aria-label="Fermer"></button>
      </div>

      <div class="card-body">
        <p class="small text-muted mb-3">
          Mère : <strong>{{ mere.prenom }} {{ mere.nom }}</strong>. Chaque bébé a son propre dossier ;
          la mère devient sa responsable principale. Sans prénom, un nom provisoire est donné
          (« Bébé de {{ mere.prenom }} »), à compléter plus tard.
        </p>

        <div class="mb-3">
          <label class="form-label fw-semibold me-2">Nombre de bébés</label>
          <div class="btn-group btn-group-sm">
            <button v-for="n in 3" :key="n" type="button" class="btn" :class="bebes.length === n ? 'btn-primary' : 'btn-outline-primary'" @click="nombre(n)">
              {{ n === 1 ? '1' : n === 2 ? '2 (jumeaux)' : '3 (triplés)' }}
            </button>
          </div>
        </div>

        <div v-for="(b, i) in bebes" :key="i" class="border rounded p-3 mb-3">
          <div class="fw-semibold mb-2" v-if="bebes.length > 1">Bébé {{ i + 1 }}</div>
          <div class="row g-2">
            <div class="col-md-4">
              <label class="form-label small mb-1">Sexe *</label>
              <div>
                <div class="form-check form-check-inline">
                  <input :id="'m' + i" class="form-check-input" type="radio" value="Masculin" v-model="b.genre">
                  <label class="form-check-label" :for="'m' + i">Garçon</label>
                </div>
                <div class="form-check form-check-inline">
                  <input :id="'f' + i" class="form-check-input" type="radio" value="Feminin" v-model="b.genre">
                  <label class="form-check-label" :for="'f' + i">Fille</label>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Prénom (si déjà choisi)</label>
              <input class="form-control form-control-sm" v-model="b.prenom" placeholder="Laisser vide si pas encore choisi">
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Nom de famille</label>
              <input class="form-control form-control-sm" v-model="b.nom" :placeholder="nomParDefaut">
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Date de naissance</label>
              <input type="date" class="form-control form-control-sm" :max="aujourdhui" v-model="b.dateNaissance">
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Heure</label>
              <input type="time" class="form-control form-control-sm" v-model="b.heureNaissance">
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Poids (grammes)</label>
              <input type="number" min="300" max="9000" step="10" class="form-control form-control-sm" v-model.number="b.poidsNaissance" placeholder="ex. 3100">
            </div>
          </div>
        </div>

        <details class="mb-2">
          <summary class="fw-semibold small">Père (facultatif)</summary>
          <div class="row g-2 mt-1">
            <div class="col-md-4"><input class="form-control form-control-sm" v-model="pere.nom" placeholder="Nom"></div>
            <div class="col-md-4"><input class="form-control form-control-sm" v-model="pere.prenom" placeholder="Prénom"></div>
            <div class="col-md-4">
              <input type="tel" class="form-control form-control-sm" :class="{ 'is-invalid': !telephoneValide(pere.telephone) }" v-model="pere.telephone" placeholder="Téléphone">
            </div>
          </div>
          <small class="text-muted">Si le nom du père est indiqué, il sert de nom de famille par défaut.</small>
        </details>

        <div v-if="erreur" class="alert alert-danger py-2 small mb-0">{{ erreur }}</div>
      </div>

      <div class="card-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border" @click="fermer">Annuler</button>
        <button type="button" class="btn btn-success" :disabled="enCours" @click="enregistrer">
          <span v-if="enCours" class="spinner-border spinner-border-sm me-1"></span>
          Enregistrer {{ bebes.length > 1 ? 'les ' + bebes.length + ' bébés' : 'le bébé' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import { telephoneValide } from '@/utils/validation'

const aujourdhui = () => { const d = new Date(); const z = x => String(x).padStart(2, '0'); return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}` }
const bebeVide = () => ({ genre: '', prenom: '', nom: '', dateNaissance: aujourdhui(), heureNaissance: '', poidsNaissance: null })

export default {
  name: 'NouveauNeModal',
  props: {
    mere: { type: Object, required: true },
  },
  emits: ['fermer', 'cree'],
  data() {
    return { bebes: [bebeVide()], pere: { nom: '', prenom: '', telephone: '' }, enCours: false, erreur: '', aujourdhui: aujourdhui() }
  },
  computed: {
    nomParDefaut() { return (this.pere.nom || '').trim() || this.mere.nom || '' },
  },
  methods: {
    telephoneValide,
    nombre(n) {
      while (this.bebes.length < n) this.bebes.push(bebeVide())
      this.bebes.splice(n)
    },
    fermer() {
      if (!this.enCours) this.$emit('fermer')
    },
    async enregistrer() {
      this.erreur = ''
      const sansSexe = this.bebes.findIndex(b => !b.genre)
      if (sansSexe >= 0) {
        this.erreur = this.bebes.length > 1 ? `Indiquez le sexe du bébé ${sansSexe + 1}.` : 'Indiquez le sexe du bébé.'
        return
      }
      if (!telephoneValide(this.pere.telephone)) {
        this.erreur = 'Le téléphone du père n\'est pas valide.'
        return
      }
      this.enCours = true
      try {
        const corps = {
          bebes: this.bebes.map(b => ({
            genre: b.genre, prenom: b.prenom, nom: b.nom, dateNaissance: b.dateNaissance,
            heureNaissance: b.heureNaissance || null, poidsNaissance: b.poidsNaissance || null,
          })),
          pere: (this.pere.nom || '').trim() ? this.pere : null,
        }
        const r = await api.custom(`patients/${this.mere.id}/nouveau-nes`, 'POST', corps)
        const crees = r.data.data || []
        notifier(crees.length > 1 ? `${crees.length} nouveau-nés enregistrés.` : `${crees[0]?.nomComplet || 'Nouveau-né'} enregistré.`, 'success')
        this.$emit('cree', r.data)
      } catch (e) {
        this.erreur = messageErreur(e, "Le nouveau-né n'a pas pu être enregistré.")
      } finally {
        this.enCours = false
      }
    },
  },
}
</script>

<style scoped>
.nn-fond { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; overflow-y: auto; padding: 24px 12px; }
.nn-boite { width: 100%; max-width: 760px; }
</style>
